// packages/console/src/components/panels/LiveDestinationsPanel.tsx
//
// Destinations panel for the LIVE console. It drives the control plane: the
// configured push targets for the selected stream (from /api/live), each with a
// delete control, plus an inline form to add one. Live push health is overlaid
// from the telemetry stream when it reports a matching target, so a configured
// destination reads its real send status once the stream is pushing.
//
// Honest state (R10): with no stream selected the panel invites creating one; a
// configured target whose health the stream has not reported yet idles rather
// than claiming a green send. The design's fixed YouTube/SRT/CDN list is gone.

import { useState } from "react";
import { Panel, Dot } from "../Parts";
import type { Tone } from "../Parts";
import { Icon } from "../Icons";
import type { AddDestinationInput, LiveDestinationRec, LivePushProto } from "../../api/liveControl";

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  borderRadius: "var(--r-sm)",
  border: "1px solid var(--line-strong)",
  background: "var(--bg-base)",
  color: "var(--fg1)",
  font: "400 11px var(--font-mono)",
};

export function LiveDestinationsPanel({
  destinations,
  healthById,
  onAdd,
  onRemove,
  disabled,
  busy,
}: {
  destinations: LiveDestinationRec[];
  healthById: Map<string, Tone>;
  onAdd: (input: AddDestinationInput) => void;
  onRemove: (destId: string) => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [proto, setProto] = useState<LivePushProto>("rtmps");
  const [url, setUrl] = useState("");
  const [key, setKey] = useState("");

  const submit = () => {
    if (!url.trim()) return;
    onAdd({ name: name.trim() || "Destination", proto, url: url.trim(), streamKey: key.trim() });
    setName("");
    setUrl("");
    setKey("");
    setAdding(false);
  };

  return (
    <Panel
      title="Destinations"
      style={{ flex: "none" }}
      right={
        <button
          onClick={() => !disabled && setAdding((v) => !v)}
          title={disabled ? "Create a stream to add destinations" : adding ? "Cancel" : "Add destination"}
          disabled={disabled}
          style={{
            display: "inline-flex",
            padding: 2,
            background: "transparent",
            border: "none",
            color: disabled ? "var(--fg4)" : "var(--fg3)",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <Icon name={adding ? "x" : "plus"} size={14} />
        </button>
      }
    >
      {adding && !disabled && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 12 }}>
          <input style={inputStyle} placeholder="Name (e.g. YouTube)" value={name} onChange={(e) => setName(e.target.value)} />
          <div style={{ display: "flex", gap: 7 }}>
            <select
              value={proto}
              onChange={(e) => setProto(e.target.value as LivePushProto)}
              style={{ ...inputStyle, width: 90, flex: "none" }}
            >
              <option value="rtmps">rtmps</option>
              <option value="srt">srt</option>
            </select>
            <input style={inputStyle} placeholder="Push URL" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <input style={inputStyle} placeholder="CDN stream key (optional)" value={key} onChange={(e) => setKey(e.target.value)} />
          <button
            onClick={submit}
            disabled={busy || !url.trim()}
            style={{
              alignSelf: "flex-start",
              padding: "6px 12px",
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--line-strong)",
              background: "transparent",
              color: !url.trim() ? "var(--fg4)" : "var(--blue-400)",
              font: "var(--t-btn)",
              letterSpacing: "var(--ls-btn)",
              textTransform: "uppercase",
              cursor: busy || !url.trim() ? "not-allowed" : "pointer",
            }}
          >
            Add
          </button>
        </div>
      )}
      {destinations.length === 0 ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
          {disabled ? "Create a stream to add destinations" : "No push targets configured"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {destinations.map((d) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                paddingBottom: 10,
                borderBottom: "1px solid var(--line)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Dot tone={healthById.get(d.id) ?? "idle"} />
                <span style={{ font: "var(--t-label)", color: "#fff", flex: 1 }}>{d.name}</span>
                <span style={{ font: "500 9px var(--font-sans)", letterSpacing: ".12em", color: "var(--blue-400)" }}>
                  {d.proto}
                </span>
                <button
                  onClick={() => onRemove(d.id)}
                  disabled={busy}
                  title="Remove destination"
                  style={{
                    display: "inline-flex",
                    padding: 2,
                    background: "transparent",
                    border: "none",
                    color: "var(--fg4)",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
              <div style={{ font: "400 10px var(--font-mono)", color: "var(--fg4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.url}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
